import React, { useCallback, useEffect, useState } from 'react';
import { getApiBaseUrl } from '../../config/apiBase';
import { adminFetchInit } from '../../config/adminApi';
import { ADMIN_BTN_GHOST, ADMIN_CARD, ADMIN_SECTION_TITLE } from './adminTheme';

const BUCKET_STYLES = {
  name: 'border-sky-400/40 bg-sky-950/50 text-sky-100',
  email: 'border-violet-400/40 bg-violet-950/50 text-violet-100',
  phone: 'border-amber-400/40 bg-amber-950/45 text-amber-100',
  due_diligence: 'border-emerald-400/40 bg-emerald-950/45 text-emerald-100',
  ai_preprocess: 'border-cyan-400/40 bg-cyan-950/45 text-cyan-100',
  other: 'border-slate-400/35 bg-slate-900/70 text-slate-200',
};

const BUCKET_ORDER = ['name', 'email', 'phone', 'due_diligence', 'ai_preprocess', 'other'];

/**
 * Analytics card: live + peak parallel OSINT searches by type.
 */
export default function AdminParallelSearchesCard() {
  const base = getApiBaseUrl();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${base}/admin/analytics/parallel-searches`, adminFetchInit());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    load();
    const id = window.setInterval(load, 8000);
    return () => window.clearInterval(id);
  }, [load]);

  const byBucket = data?.by_bucket || {};
  const peaks = data?.peaks || {};
  const labels = data?.bucket_labels || {};
  const capacity = data?.capacity || {};
  const current = Number(data?.current || 0);
  const peakTotal = Number(peaks.total || 0);

  return (
    <section className={ADMIN_CARD}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className={ADMIN_SECTION_TITLE}>Parallel searches</h2>
          <p className="mt-1 max-w-2xl text-xs text-slate-400">
            Live concurrency by scan type, plus session peaks. Capacity hint:{' '}
            <span className="font-mono text-slate-300">
              CORPORATE_IO_MAX_WORKERS={capacity.corporate_io_max_workers ?? '—'}
            </span>
          </p>
        </div>
        <button type="button" className={ADMIN_BTN_GHOST} onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error ? <p className="mb-3 text-xs font-semibold text-rose-300">{error}</p> : null}

      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-cyan-400/30 bg-cyan-950/35 p-4">
          <p className="text-[10px] font-black uppercase tracking-wider text-cyan-200/80">Now active</p>
          <p className="mt-2 text-4xl font-black tabular-nums text-cyan-50">{current}</p>
        </div>
        <div className="rounded-xl border border-fuchsia-400/30 bg-fuchsia-950/35 p-4">
          <p className="text-[10px] font-black uppercase tracking-wider text-fuchsia-200/80">Peak concurrent</p>
          <p className="mt-2 text-4xl font-black tabular-nums text-fuchsia-50">{peakTotal}</p>
          {peaks.updated_at ? (
            <p className="mt-2 font-mono text-[10px] text-fuchsia-200/60">since {peaks.updated_at}</p>
          ) : null}
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {BUCKET_ORDER.map((id) => {
          const live = Number(byBucket[id] || 0);
          const peak = Number(peaks[id] || 0);
          const tone = BUCKET_STYLES[id] || BUCKET_STYLES.other;
          return (
            <div key={id} className={`rounded-xl border p-3 ${tone}`}>
              <p className="text-[10px] font-black uppercase tracking-wider opacity-80">
                {labels[id] || id}
              </p>
              <div className="mt-2 flex items-end justify-between gap-2">
                <div>
                  <p className="text-[10px] uppercase tracking-wide opacity-70">Live</p>
                  <p className="text-2xl font-black tabular-nums">{live}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wide opacity-70">Max</p>
                  <p className="text-xl font-black tabular-nums">{peak}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {(data?.active || []).length > 0 ? (
        <div className="mt-4 overflow-auto rounded-lg border border-white/10">
          <table className="w-full border-collapse text-left text-xs">
            <thead className="bg-slate-950/80">
              <tr className="border-b border-white/10 text-[10px] font-black uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Bucket</th>
                <th className="px-3 py-2">Path</th>
                <th className="px-3 py-2">Elapsed</th>
                <th className="px-3 py-2">Id</th>
              </tr>
            </thead>
            <tbody>
              {data.active.map((row) => (
                <tr key={row.id} className="border-b border-white/5 text-slate-300">
                  <td className="px-3 py-2 font-semibold">{row.bucket}</td>
                  <td className="max-w-md truncate px-3 py-2 font-mono text-[11px]" title={row.path}>
                    {row.path}
                  </td>
                  <td className="px-3 py-2 font-mono tabular-nums">
                    {row.elapsed_s != null ? `${row.elapsed_s}s` : '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{row.id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mt-4 text-xs text-slate-500">No searches in flight right now.</p>
      )}
    </section>
  );
}
